import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { GenericTable, RoleValidator } from "../../../Components/Common";
import {
  MATERIAL_LABELS_MRR,
  materialColumns,
  TOAST_MESSAGES,
  MRR_STATUS,
  getMRRStatusColor,
  TABS,
} from "./constants";
import { formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import GRNComparison from "./GRN-MRR";
import DNComparison from "./DN-MRR";
import { VIEW_MATERIAL_REQUESTS } from "../../../routes";
import { useMRR } from "../Hooks/useMRR";
import { getPriorityStatusColor, ROLES } from "../../../Constants/Strings";
import { UserContext } from "../../../Context";

const MRRDetailsPage = () => {
  const { fetchMRRById, updateMRRDetails } = useMRR();
  const { instanceId } = useParams();
  const navigate = useNavigate();

  const [materialData, setMaterialData] = useState([]);
  const [mrrData, setMrrData] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.DETAILS);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(UserContext);

  //  Load MRR details
  const loadMRRDetails = async () => {
    setLoading(true);
    const data = await fetchMRRById(instanceId);
    if (data) {
      setMrrData(data);
      setMaterialData(data.materials || []);
    } else {
      setMrrData(null);
      setMaterialData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMRRDetails();
  }, [instanceId]);

  // Update MRR status
  const handleStatusChange = async (newStatus) => {
    const updatedData = await updateMRRDetails(instanceId, { status: newStatus });
    if (updatedData) setMrrData(updatedData);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <p className="text-gray-600">Loading MRR details...</p>
      </div>
    );
  }

  if (!mrrData) {
    return (
      <div className="p-4 text-red-500">{TOAST_MESSAGES.MRR_NO_DATA}</div>
    );
  }

  // Custom render for material columns
  const renderFields = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div>
            {row.product_name}
            <span className="text-xs text-gray-500 flex flex-wrap">
              {row.specification}
            </span>
            <span className="text-xs text-gray-500 flex flex-wrap">
              {row.UOM}
            </span>
          </div>
        );
      case "quantity":
        return Number(row.quantity);
      default:
        return row[column] || "N/A";
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-start gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(VIEW_MATERIAL_REQUESTS)}
            className="text-primary hover:text-green-600"
          >
            <HiOutlineArrowLeft size={20} />
          </button>
          <h2 className="text-xl text-primary font-semibold">
            View Material Requisition Request
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-gray-200">
          {Object.values(TABS).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium ${
                activeTab === tab
                  ? "border-b-2 border-primary text-primary"
                  : "text-gray-500"
              }`}
            >
              {tab === TABS.DETAILS
                ? "Details"
                : tab === TABS.GRN
                ? "GRN Comparison"
                : "Dispatch Comparison"}
            </button>
          ))}
        </div>
      </div>

      {/* DETAILS TAB */}
      {activeTab === TABS.DETAILS && (
        <div className="flex flex-col md:flex-row justify-between w-full px-4 gap-6">
          {/* MRR DETAILS */}
          <div className="md:w-[40%] bg-white border-gray-200 md:pr-8 md:mt-4">
            <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
              <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
              MRR Details
            </h3>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <p className="text-sm text-gray-500">MRR Code</p>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {mrrData?.mrr_code || "-"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Requested By Site</p>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {mrrData?.warehouse_name || "-"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Raised By</p>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {mrrData?.created_by || "-"}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Raised On</p>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {formatDateDDMMYYYY(mrrData?.created?.split("T")[0] || "-")}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Expected Delivery</p>
                <p className="font-semibold text-gray-800 mt-0.5">
                  {formatDateDDMMYYYY(mrrData?.estimated_delivery_date || "-")}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p
                  className={`inline-block mt-0.5 px-2 py-1 text-xs font-semibold rounded-full ${getMRRStatusColor(
                    mrrData?.status
                  )}`}
                >
                  {toProperCase(mrrData?.status || "-")}
                </p>
              </div>
              {/* Priority (Right-aligned) */}
              <div className="col-span-2 flex justify-start">
                <div className="flex flex-col items-start">
                  <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                    Priority
                  </label>
                  <span
                    className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getPriorityStatusColor(
                      mrrData.priority
                    )}`}
                  >
                    {toProperCase(mrrData.priority || "")}
                  </span>
                </div>
              </div>

              {/* Notes (Full Width) */}
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Notes</p>
                <div className="mt-0.5 bg-gray-50 px-2 py-3 rounded-lg border border-gray-100">
                  <p className="font-medium text-gray-800 whitespace-pre-wrap">
                    {mrrData?.notes || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* MATERIAL TABLE */}
          {materialData.length > 0 && (
            <div className="mt-4 md:w-[60%] md:border-l-2 md:pl-8">
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
                MRR Materials
              </h3>
              {/* Desktop Table */}
              <div className="hidden md:block h-[42vh] overflow-y-auto">
                <GenericTable
                  tableColumns={materialColumns}
                  columnLabels={MATERIAL_LABELS_MRR}
                  tableRows={materialData}
                  renderField={renderFields}
                  enableMobileCardView={false}
                />
              </div>
              {/* Mobile Card View */}
              <div className="md:hidden flex flex-col gap-4 h-[25vh] overflow-y-auto">
                {materialData.map((m) => (
                  <div
                    key={m.id}
                    className="relative p-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex flex-col max-w-[60%]">
                        <div className="font-semibold text-gray-900 text-base">
                          {m.product_name}
                        </div>
                        {m.specification && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {m.specification}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary">
                          {Number(m.quantity) % 1 === 0
                            ? Number(m.quantity)
                            : Number(m.quantity).toFixed(2)}
                        </div>
                        {m.UOM && (
                          <div className="text-xs font-medium text-gray-500">
                            {m.UOM}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="my-2 border-t border-gray-100" />
                    {m.description && (
                      <div className="text-sm text-gray-700 leading-snug">
                        <span className="font-medium text-gray-600">
                          Delivery Note:
                        </span>{" "}
                        <p className="overflow-x-auto">{m.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* ACTION BUTTONS */}
              <div className="flex justify-end gap-3 mt-6">
                {/* Project Manager — approve/reject */}
                <RoleValidator
                  allowedRoles={[ROLES.PROJECT_MANAGER, ROLES.SITE_ENGINEER]}
                  mode="hide"
                >
                  {mrrData.status === MRR_STATUS.PENDING_FOR_APPROVAL && (
                    <>
                      <button
                        onClick={() => handleStatusChange(MRR_STATUS.APPROVED)}
                        disabled={user.role === ROLES.SITE_ENGINEER}
                        className={`px-4 py-2 rounded-md font-medium text-white transition 
                                bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-300
                                disabled:bg-green-300 disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleStatusChange(MRR_STATUS.REJECTED)}
                        disabled={user.role === ROLES.SITE_ENGINEER}
                        className={`px-4 py-2 rounded-md font-medium text-white transition 
                        bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-300
                        disabled:bg-red-300 disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </RoleValidator>

                {/* SE — Sent to Inventory / Completed */}
                <RoleValidator
                  allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_MANAGER]}
                  mode="hide"
                >
                  {mrrData.status === MRR_STATUS.APPROVED && (
                    <button
                      onClick={() =>
                        handleStatusChange(MRR_STATUS.SENT_TO_INVENTORY)
                      }
                      disabled={user.role === ROLES.PROJECT_MANAGER}
                      className={`px-4 py-2 rounded-md font-medium text-white transition 
                    bg-violet-600 hover:bg-violet-700 focus:ring-2 focus:ring-violet-300
                    disabled:bg-violet-300 disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      Sent to Inventory
                    </button>
                  )}
                  {mrrData.status === MRR_STATUS.SENT_TO_INVENTORY && (
                    <button
                      onClick={() => handleStatusChange(MRR_STATUS.COMPLETED)}
                      disabled={user.role === ROLES.PROJECT_MANAGER}
                      className={`px-4 py-2 rounded-md font-medium text-white transition 
                    bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-300
                    disabled:bg-blue-300 disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      Mark as Completed
                    </button>
                  )}
                </RoleValidator>
              </div>
            </div>
          )}
        </div>
      )}

      {/* GRN & DISPATCH COMPARISON */}
      {activeTab === TABS.GRN && (
        <GRNComparison instanceId={instanceId} mrrData={mrrData} />
      )}
      {activeTab === TABS.DN && (
        <DNComparison instanceId={instanceId} mrrData={mrrData} />
      )}
    </div>
  );
};

export default MRRDetailsPage;
